import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Flame, Award } from "lucide-react";

/**
 * Weekly streak card for the Weight page. Shows current streak, next-badge
 * target, and any earned badges. Silent-fails if the endpoint isn't reachable.
 */
export default function WeightStreakCard({ refreshToken = 0 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/weights/streak")
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [refreshToken]);

  if (!data) return null;
  const { current_streak_weeks, total_weeks_logged, earned_badges, next_badge, weeks_to_next } = data;
  const pctToNext = next_badge && next_badge.weeks > 0
    ? Math.min(100, (current_streak_weeks / next_badge.weeks) * 100)
    : 100;

  return (
    <div
      className="bg-white border border-slate-200 p-6 rounded-none"
      data-testid="weight-streak-card"
    >
      <div className="flex items-baseline justify-between mb-4">
        <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-1.5">
          <Flame className="w-3 h-3 text-[#0D9488]" /> // Streak
        </div>
        <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-400">
          {total_weeks_logged} total weeks logged
        </div>
      </div>

      <div className="flex items-baseline gap-6">
        <div>
          <div className="font-display font-black text-6xl leading-none text-[#0D9488] tabular-nums" data-testid="streak-weeks-count">
            {current_streak_weeks}
          </div>
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mt-2">
            {current_streak_weeks === 1 ? "week in a row" : "weeks in a row"}
          </div>
        </div>
        {next_badge && (
          <div className="flex-1 min-w-0">
            <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              Next badge · {weeks_to_next} week{weeks_to_next === 1 ? "" : "s"} to go
            </div>
            <div className="text-white text-sm font-semibold mb-2">
              <span className="mr-1">{next_badge.emoji}</span>{next_badge.label}
            </div>
            <div className="h-2 border border-slate-200 relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-[#0D9488]"
                style={{ width: `${pctToNext}%` }}
                data-testid="streak-progress-fill"
              />
            </div>
          </div>
        )}
      </div>

      {earned_badges && earned_badges.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-200">
          <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 flex items-center gap-1.5">
            <Award className="w-3 h-3 text-[#0D9488]" /> Earned
          </div>
          <div className="flex flex-wrap gap-2">
            {earned_badges.map((b) => (
              <div
                key={b.weeks}
                className="inline-flex items-center gap-1.5 border border-[#0D9488]/40 bg-[#0D9488]/5 px-2.5 py-1"
                data-testid={`streak-badge-${b.weeks}`}
              >
                <span>{b.emoji}</span>
                <span className="font-mono-num text-[10px] uppercase tracking-widest text-[#0D9488]">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
